import React from "react"
import { useTranslation } from "react-i18next"
import { Link as RRLink } from "react-router"

import { useSiteNameColumn } from "@/utils/grid-columns/use-site-name-column"
import { renderRowNumberPaginated } from "@/utils/render-row-number-paginated"
import { useUrl } from "@/utils/url"

import { Link } from "@mui/material"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro"

import { baseSiteNumberColumnDef } from "../../../models"
import type { BaseSiteRecord } from "../../../services/use-get-site-explorer"

type CommonSiteGridColumnName = "rowNumber" | "siteName"

export const useCommonSiteGridColumns = <
  TSiteRow extends BaseSiteRecord,
>(): Record<CommonSiteGridColumnName, GridColDef<TSiteRow>> => {
  const { t } = useTranslation()
  const { buildSiteUrl } = useUrl()
  const siteNameColumn = useSiteNameColumn<TSiteRow>()

  const columns = React.useMemo(() => {
    const columnDefMap: Record<
      CommonSiteGridColumnName,
      GridColDef<TSiteRow>
    > = {
      rowNumber: {
        ...baseSiteNumberColumnDef,
        description: t("gridColumn.rowNumber.description"),
        disableColumnMenu: true,
        field: "rowNumber",
        headerName: t("gridColumn.rowNumber.name"),
        hideable: false,
        minWidth: 56,
        renderCell: (params: GridRenderCellParams<TSiteRow>) =>
          renderRowNumberPaginated({
            pageIndex: params.row.pageIndex,
            page: params.row.page,
            pageSize: params.row.pageSize,
          }),
        resizable: false,
        sortable: false,
        width: 56,
      },
      siteName: {
        ...siteNameColumn,
        flex: 0,
        hideable: false,
        renderCell: (params: GridRenderCellParams<TSiteRow>) => {
          return (
            <Link
              component={RRLink}
              noWrap
              tabIndex={params.tabIndex}
              to={buildSiteUrl(params.row.site_id.toString())}
            >
              {params.value}
            </Link>
          )
        },
        width: 240,
      },
    }

    return columnDefMap
  }, [buildSiteUrl, siteNameColumn, t])

  return columns
}
